import SolutionReviewForm from '../components/admin-account/solution-moderation/SolutionReviewForm';
import { compose, graphql } from 'react-apollo';
import gql from 'graphql-tag';
import withCommand from './withCommand';
import { sent_status_email } from './SolutionSubmissionFormContainer';

import SolutionStatus from '../components/common/SolutionStatus';

export const getSolutionQuery = gql`
  query($solutionId: ID, $listingVersion: String) {
    solutionDetails(id: $solutionId, listingVersion: $listingVersion) {
      id
      listingVersion
      apigee_id
      developerId
      developerName
      activateOnly
      developerAgreement
      dnpAppName
      orgId
      status
      submittedOn
      lastUpdatedBy
      lastUpdatedOn
      registeredOn
      solutionType
      oneToOneUI
      solution_visibility {
        solutionVisibilityType
        privateSolutionUsers
      }
      app_subscriptions {
        appSubscriptionsType
      }
      overview {
        name
        domain
        category
        applicationArea
        searchKeywords
        version
        briefDescription
        detailedDescription
        publisherName
        website
        solutionWebsite
        outageNotificationsEmail
        copyright
        privacyPolicyUrl
        termsAndConditionsUrl
        solutionLogo
        videos {
          id
          title
          url
          description
        }
        featuredImage
        galleryImages {
          id
          galleryImage
          imagePositionIndex
        }
      }
      highlights {
        highlight_list {
          id
          description
        }
      }
      provisioning_interface {
        endpointUrl
        redirectURL
        provisioningtype
        entityID
        subscriptionID
      }
      subscription_notifications {
        email
        endpointUrl
        apiNotification
      }
      support {
        tutorials_and_documentation {
          id
          title
          url
          purpose
        }
        supportMessage
        supportCenterUrl
        phoneNumber
        emailAddress
        supportCommunityUrl
      }
      worksWithIds
      appPublishingFee
      features {
        id
        title
        description
        featureImage
      }

      plans {
        id
        isTheFreeTrial
        isFreeTrialEnabled
        position
        planName
        planDescription
        freeTrialPeriod
        freeTrialPeriodType
        billingType
        transactionalTypeOptions
        includedTransactionalType
        includedTransactionalFee
        billingFrequency
        basePrice
        includedUnitOfMeasure
        includedUnitQuantity
        additionalUnitPrice
        additionalUnitOfMeasure
        features {
          id
          title
          description
          featureImage
        }
      }
      comments {
        userId
        commentDate
        commentText
        action
      }
      subscription_management {
        allowSubscriptions
      }
      data_rights {
        captureDataRightsConsent
      }
      contract_notifications {
        contractNotificationsType
      }
      payment_info {
        paymentMode
        paymentOption {
          stripe
          cdk
        }
      }
      un_authorized_signon {
        isEnabled
        signOnRole
      }
      additional_info {
        catalog_number
        is_vision_case_required
        vision_case_queue
        uninstall_queue
      }
      custom_consent {
        customConsentForm
        fileName
        selectedApis
      }
      registration_details {
        api_details {
          id
          name
          displayName
          description
          isOptional
          apiType
          asyncIntegrationId
          providers {
            id
            entityId
            name
          }
        }
        apiKey
        apiSecret
        keyIssuedOn
        keyExpiresOn
        callbackUrl
      }
      partner_program_registration_details {
        partner_api_details {
          id
          applicationName
          description
          dataProvider
        }
      }
      address {
        street_address
        city
        country
        countrySubDivisionCode
        postalCode
      }
      listingVersionUpdates {
        description
        updates {
          id
          updateText
        }
      }
      isFeatured
      stripePriceId
      listingFeeHistory {
        userEmail
        prevListingFee
        newListingFee
        updatedOn
      }
    }
  }
`;

export const updateSolutionStatusQuery = gql`
  mutation(
    $id: String!
    $listingVersion: String!
    $status: String
    $lastUpdatedBy: String
    $lastUpdatedOn: String
    $comments: [CommentCmd]
    $developerEmail: String!
    $orgId: String!
    $appName: String!
    $attributeName: String!
    $attributeValue: String!
  ) {
    updateSolutionStatus(
      command: {
        id: $id
        listingVersion: $listingVersion
        status: $status
        lastUpdatedBy: $lastUpdatedBy
        lastUpdatedOn: $lastUpdatedOn
        comments: $comments
        developerEmail: $developerEmail
        orgId: $orgId
        appName: $appName
        attributeName: $attributeName
        attributeValue: $attributeValue
      }
    ) {
      id
      listingVersion
      status
      error {
        message
      }
    }
  }
`;

export const solution_status_email_csmsg = gql`
  mutation(
    $to: [csmsgEmailInput]!
    $emailSubject: String!
    $emailType: String!
    $firstName: String!
    $lastName: String
    $solutionName: String
    $solutionId: String
  ) {
    sendEmailCSMG(
      command: {
        to: $to
        subject: $emailSubject
        withTemplate: {
          emailType: $emailType
          solutionName: $solutionName
          solutionId: $solutionId
        }
        userDetails: { firstName: $firstName, lastName: $lastName }
      }
    ) {
      emailResponse
    }
  }
`;

export const mapDataToProps = ({ data }) => {
  // Helper function to get prevListing with System recall logic, System recall happens when
  // ISV adds an api to existing DNP app whose listing is already in Approved/In Review state
  // Please note in normal ISV recall we should work on same listing version
  // but in case of System recall we should work on next listing version.
  const getPrevListing = solutionDetails => {
    if (!solutionDetails || !solutionDetails.length) return null;

    const sortedSolutions = solutionDetails.sort(
      (a, b) => parseInt(a.listingVersion, 10) - parseInt(b.listingVersion, 10)
    );

    const latestSolution = sortedSolutions[sortedSolutions.length - 1];
    const latestListingVersion = parseInt(latestSolution.listingVersion, 10);

    if (latestListingVersion <= 1) return null;

    const previousVersion = sortedSolutions.find(
      solution =>
        parseInt(solution.listingVersion, 10) === latestListingVersion - 1
    );

    if (previousVersion && previousVersion.status === SolutionStatus.RECALLED) {
      const lastComment =
        previousVersion.comments && previousVersion.comments.length > 0
          ? previousVersion.comments[previousVersion.comments.length - 1]
          : null;

      // we rely on comments in listing to check history of that listing.
      if (
        lastComment &&
        lastComment.action === SolutionStatus.RECALLED &&
        lastComment.userId === 'System'
      ) {
        // Find the last withdrawn version
        const withdrawnSolutions = sortedSolutions.filter(
          solution => solution.status === SolutionStatus.WITHDRAWN
        );
        const lastWithdrawnVersion =
          withdrawnSolutions.length > 0
            ? withdrawnSolutions.sort(
                (a, b) =>
                  parseInt(b.listingVersion, 10) -
                  parseInt(a.listingVersion, 10)
              )[0]
            : null;

        // Find the last published version
        const publishedSolutions = sortedSolutions.filter(
          solution => solution.status === SolutionStatus.PUBLISHED
        );
        const lastPublishedVersion =
          publishedSolutions.length > 0
            ? publishedSolutions.sort(
                (a, b) =>
                  parseInt(b.listingVersion, 10) -
                  parseInt(a.listingVersion, 10)
              )[0]
            : null;

        // Choose the most recent between withdrawn and published (whichever has higher listing version)
        let result = null;
        if (lastWithdrawnVersion && lastPublishedVersion) {
          result =
            parseInt(lastWithdrawnVersion.listingVersion, 10) >
            parseInt(lastPublishedVersion.listingVersion, 10)
              ? lastWithdrawnVersion
              : lastPublishedVersion;
          return result;
        } else if (lastWithdrawnVersion) {
          result = lastWithdrawnVersion;
          return result;
        } else if (lastPublishedVersion) {
          result = lastPublishedVersion;
          return result;
        }
      }
    }

    // Default behavior: return the immediate previous version
    const defaultPrevListing = previousVersion || null;
    return defaultPrevListing;
  };

  return {
    solution:
      (data &&
        data.solutionDetails &&
        data.solutionDetails.length && [
          data.solutionDetails.sort(
            (a, b) =>
              parseInt(a.listingVersion, 10) - parseInt(b.listingVersion, 10)
          )[data.solutionDetails.length - 1]
        ]) ||
      [],

    prevListing: getPrevListing(data?.solutionDetails),
    loading: data.loading
  };
};

export default compose(
  graphql(getSolutionQuery, {
    props: mapDataToProps,
    options: ({ solutionId, listingVersion }) => ({
      variables: {
        solutionId: solutionId,
        listingVersion: listingVersion
      }
    })
  }),
  compose(
    graphql(updateSolutionStatusQuery, {
      props: ({ mutate }) => ({
        updateSolutionStatus: (
          id,
          listingVersion,
          status,
          lastUpdatedBy,
          lastUpdatedOn,
          comments,
          developerEmail,
          orgId,
          appName,
          attributeName,
          attributeValue
        ) =>
          mutate({
            variables: {
              id: id,
              listingVersion: listingVersion,
              status: status,
              lastUpdatedBy: lastUpdatedBy,
              lastUpdatedOn: lastUpdatedOn,
              comments: comments,
              developerEmail: developerEmail,
              orgId: orgId,
              appName: appName,
              attributeName: attributeName,
              attributeValue: attributeValue
            }
          })
      })
    }),
    withCommand({ name: 'updateSolutionStatus' })
  ),
  compose(
    graphql(sent_status_email, {
      props: ({ mutate }) => ({
        sendEmail: (
          emailAddress,
          emailSubject,
          emailType,
          solutionName,
          solutionReviewComments,
          firstName,
          lastName
        ) =>
          mutate({
            variables: {
              emailAddress: emailAddress,
              emailSubject: emailSubject,
              emailType: emailType,
              solutionName: solutionName,
              solutionReviewComments: solutionReviewComments,
              firstName: firstName,
              lastName: lastName
            }
          })
      })
    }),
    withCommand({ name: 'sendEmail' })
  ),
  compose(
    graphql(solution_status_email_csmsg, {
      props: ({ mutate }) => ({
        sendEmailCSMG: (
          to,
          emailSubject,
          emailType,
          solutionName,
          solutionId,
          firstName,
          lastName
        ) =>
          mutate({
            variables: {
              to: to,
              emailSubject: emailSubject,
              emailType: emailType,
              firstName: firstName,
              lastName: lastName,
              solutionName: solutionName,
              solutionId: solutionId
            }
          })
      })
    }),
    withCommand({ name: 'sendEmailCSMG' })
  )
)(SolutionReviewForm);
